import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthenticationService } from '@/services/apiClient'
import { clearAllExpandedStates } from '@/utils/explorerStateStorage'

/**
 * Clears localStorage while preserving agent-specific settings (encryption, OpenAI, API keys), database filter preferences, and UI preferences
 */
function clearLocalStorageExceptEncryption() {
  // Save encryption, OpenAI, API key, database filter, and UI settings before clearing
  const settingsToPreserve: Record<string, string> = {}
  const encryptionPrefix = 'mazpin_agent_encryption_'
  const openaiPrefix = 'mazpin_agent_openai_'
  const apiKeyPrefix = 'mazpin_agent_apikey_'
  const databaseFilterKey = 'database-schema-filters'
  const sidebarCollapsedKey = 'sidebar-collapsed'
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.startsWith(encryptionPrefix) || key.startsWith(openaiPrefix) || key.startsWith(apiKeyPrefix) || key === databaseFilterKey || key === sidebarCollapsedKey)) {
      settingsToPreserve[key] = localStorage.getItem(key) || ''
    }
  }
  
  // Clear all localStorage
  localStorage.clear()
  
  // Restore preserved settings
  for (const [key, value] of Object.entries(settingsToPreserve)) {
    if (value) {
      localStorage.setItem(key, value)
    }
  }
}

export interface User {
  id: string
  username: string
  email?: string
  name: string
  role: 'admin' | 'user'
  createdAt: string
  authProvider?: 'google' | 'local'
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setToken: (token: string) => void
  clearAuth: () => void
  loadUserFromStorage: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await AuthenticationService.createLogin({
            requestBody: {
              username,
              password,
            }
          })
          
          if (response.token && response.user) {
            // Add authProvider for regular login
            const userWithAuth: User = {
              ...(response.user as User),
              authProvider: 'local'
            };
            
            // Store in both localStorage and Zustand store
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(userWithAuth));
            
            console.log('✅ [AUTH] Login successful for user:', {
              id: response.user.id,
              username: response.user.username,
              authProvider: 'local'
            })
            
            set({ 
              user: userWithAuth, 
              token: response.token, 
              isLoading: false 
            })

            // Fetch user settings after successful login
            try {
              const { useSettingsStore } = await import('./settingsStore')
              await useSettingsStore.getState().fetchSettings()
              console.log('✅ [AUTH] User settings fetched after login')
            } catch (settingsError) {
              console.error('Failed to fetch settings after login:', settingsError)
              // Don't throw - login was successful, settings fetch is optional
            }
          } else {
            // Invalid response - reset loading state and throw error
            set({ isLoading: false })
            throw new Error('Invalid login response - missing token or user data')
          }
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: () => {
        const currentUser = get().user
        console.log('🚪 [AUTH] Logging out user:', currentUser ? {
          id: currentUser.id,
          username: currentUser.username
        } : 'No user')
        
        void (async () => {
          try {
            await AuthenticationService.createLogout()
          } catch (error) {
            // Even if logout API fails, clear local state
            console.error('Logout API error:', error)
          }
        })()
        
        // Clear ALL state aggressively - complete reset like page refresh
        
        // STEP 1: Immediately clear auth state to prevent any new requests
        set({ user: null, token: null, isLoading: false })
        console.log('✅ [AUTH] Auth state cleared immediately')
        
        // STEP 2: Clear localStorage but preserve agent-specific settings
        clearLocalStorageExceptEncryption()
        // Also clear OAuth-specific storage
        sessionStorage.removeItem('oauth_redirect')
        console.log('✅ [AUTH] LocalStorage cleared (except agent settings) on logout')
        
        // STEP 3: Clear React Query cache to remove ALL cached data
        import('@/services/queryClient').then(({ queryClient }) => {
          queryClient.clear()
          // Also cancel all ongoing queries
          queryClient.cancelQueries()
          console.log('✅ [AUTH] React Query cache cleared and queries cancelled on logout')
          return
        }).catch((error) => {
          console.error('Failed to clear React Query cache on logout:', error)
        })
        
        // 2. Reset collections store to initial state
        import('./collectionsStore').then(({ useCollectionsStore }) => {
          const collectionsStore = useCollectionsStore.getState()
          collectionsStore.cache.invalidateAllCollections()
          useCollectionsStore.setState({
            collections: [],
            currentCollection: null,
            isLoading: false,
            error: null,
            loadingStates: {},
            updateQueryLocks: new Set()
          })
          console.log('✅ [AUTH] Collections store reset on logout')
          return
        }).catch((error) => {
          console.error('Failed to clear collections store on logout:', error)
        })
        
        // 3. Reset query library selection store
        import('./queryLibrarySelectionStore').then(({ useQueryLibrarySelectionStore }) => {
          useQueryLibrarySelectionStore.getState().clearSelection()
          console.log('✅ [AUTH] Query library selections cleared on logout')
          return
        }).catch((error) => {
          console.error('Failed to clear query library selections on logout:', error)
        })
        
        // 4. Reset database users selection store
        import('./databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
          useDatabaseUsersSelectionStore.getState().clearAllSelections()
          console.log('✅ [AUTH] Database users selections cleared on logout')
          return
        }).catch((error) => {
          console.error('Failed to clear database users selections on logout:', error)
        })
        
        // 5. Reset connection store completely (including persisted lastActiveConnectionId)
        import('./connectionStore').then(({ useConnectionStore }) => {
          useConnectionStore.setState({
            connections: [],
            activeConnection: null,
            lastActiveConnectionId: null,
            isConnecting: false
          })
          console.log('✅ [AUTH] Connection store reset on logout')
          return
        }).catch((error) => {
          console.error('Failed to clear connection store on logout:', error)
        })
        
        // 6. Reset settings store to defaults
        import('./settingsStore').then(({ useSettingsStore }) => {
          useSettingsStore.getState().resetSettings()
          console.log('✅ [AUTH] Settings store reset on logout')
          return
        }).catch((error) => {
          console.error('Failed to reset settings store on logout:', error)
        })
        
        // 7. Clear explorer expanded states
        clearAllExpandedStates()
        console.log('✅ [AUTH] Explorer states cleared on logout')
        
        console.log('✅ [AUTH] Complete logout state reset finished')
      },

      setUser: (user: User) => {
        set({ user })
        localStorage.setItem('user', JSON.stringify(user));
      },

      setToken: (token: string) => {
        set({ token })
        localStorage.setItem('token', token);
      },

      clearAuth: () => {
        console.log('🔄 [AUTH] Clearing auth state (e.g., due to 401 error)')
        
        // Use the same aggressive clearing as logout
        // Clear ALL state completely - complete reset like page refresh
        
        // STEP 1: Immediately clear auth state to prevent any new requests
        set({ user: null, token: null, isLoading: false })
        console.log('✅ [AUTH] Auth state cleared immediately')
        
        // STEP 2: Clear localStorage but preserve agent-specific settings
        clearLocalStorageExceptEncryption()
        // Also clear OAuth-specific storage
        sessionStorage.removeItem('oauth_redirect')
        console.log('✅ [AUTH] LocalStorage cleared (except agent settings) on clearAuth')
        
        // STEP 3: Clear React Query cache to remove ALL cached data
        import('@/services/queryClient').then(({ queryClient }) => {
          queryClient.clear()
          // Also cancel all ongoing queries
          queryClient.cancelQueries()
          console.log('✅ [AUTH] React Query cache cleared and queries cancelled on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to clear React Query cache on clearAuth:', error)
        })
        
        // 2. Reset collections store to initial state
        import('./collectionsStore').then(({ useCollectionsStore }) => {
          const collectionsStore = useCollectionsStore.getState()
          collectionsStore.cache.invalidateAllCollections()
          useCollectionsStore.setState({
            collections: [],
            currentCollection: null,
            isLoading: false,
            error: null,
            loadingStates: {},
            updateQueryLocks: new Set()
          })
          console.log('✅ [AUTH] Collections store reset on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to clear collections store on clearAuth:', error)
        })
        
        // 3. Reset query library selection store
        import('./queryLibrarySelectionStore').then(({ useQueryLibrarySelectionStore }) => {
          useQueryLibrarySelectionStore.getState().clearSelection()
          console.log('✅ [AUTH] Query library selections cleared on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to clear query library selections on clearAuth:', error)
        })
        
        // 4. Reset database users selection store
        import('./databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
          useDatabaseUsersSelectionStore.getState().clearAllSelections()
          console.log('✅ [AUTH] Database users selections cleared on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to clear database users selections on clearAuth:', error)
        })
        
        // 5. Reset connection store completely
        import('./connectionStore').then(({ useConnectionStore }) => {
          useConnectionStore.setState({
            connections: [],
            activeConnection: null,
            lastActiveConnectionId: null,
            isConnecting: false
          })
          console.log('✅ [AUTH] Connection store reset on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to clear connection store on clearAuth:', error)
        })
        
        // 6. Reset settings store to defaults
        import('./settingsStore').then(({ useSettingsStore }) => {
          useSettingsStore.getState().resetSettings()
          console.log('✅ [AUTH] Settings store reset on clearAuth')
          return
        }).catch((error) => {
          console.error('Failed to reset settings store on clearAuth:', error)
        })
        
        // 7. Clear explorer expanded states
        clearAllExpandedStates()
        console.log('✅ [AUTH] Explorer states cleared on clearAuth')
        
        console.log('✅ [AUTH] Complete clearAuth state reset finished')
      },

      loadUserFromStorage: () => {
        // Since we're using persist middleware, the state is already loaded
        // This function can be used to sync with localStorage if needed
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        
        if (token && userStr) {
          try {
            const parsed = JSON.parse(userStr) as unknown;
            const user = (parsed && typeof parsed === 'object') ? ({
              id: typeof (parsed as { id?: unknown }).id === 'string' ? (parsed as { id?: string }).id! : '',
              username: typeof (parsed as { username?: unknown }).username === 'string' ? (parsed as { username?: string }).username! : '',
              email: typeof (parsed as { email?: unknown }).email === 'string' ? (parsed as { email?: string }).email! : undefined,
              name: typeof (parsed as { name?: unknown }).name === 'string' ? (parsed as { name?: string }).name! : '',
              role: ((): User['role'] => {
                const r = (parsed as Record<string, unknown>).role
                return r === 'admin' ? 'admin' : 'user'
              })(),
              createdAt: typeof (parsed as { createdAt?: unknown }).createdAt === 'string' ? (parsed as { createdAt?: string }).createdAt! : new Date().toISOString(),
              authProvider: ((): User['authProvider'] | undefined => {
                const p = (parsed as { authProvider?: unknown }).authProvider
                return p === 'google' ? 'google' : p === 'local' ? 'local' : undefined
              })(),
            } as User) : null;
            const currentState = get();
            
            // Only update if the stored values are different
            if (!currentState.token || !currentState.user) {
              set({ token, user });
            }
          } catch (error) {
            console.error('Error parsing stored user data:', error);
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
      }),
    }
  )
)
